import { useCallback, useEffect, useState, useRef } from "react";
import Select from "react-select";
import { getProfilerComparator } from "../api/quontxApi";
import GaugePlot from "./GaugePlot";
import { resetState, loadFromState, saveToState } from "../utils";

const ProfileComparatorFilters = ({
  entities,
  proxies,
  isLoading,
  propagnateData,
}) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [compareWord, setCompareWord] = useState(false);

  const [nodeA, setNodeA] = useState(null);
  const [nodeB, setNodeB] = useState(null);

  const resetFilters = () => {
    resetState("profileComparatorState", [
      {
        stateFunc: setNodeA,
        nullVal: null,
      },
      {
        stateFunc: setNodeB,
        nullVal: null,
      },
    ]);
  };

  useEffect(() => {
    loadFromState("profileComparatorState", [
      {
        stateFunc: setNodeA,
        storageObjectKey: "nodeA",
        defaultVal: null,
      },
      {
        stateFunc: setNodeB,
        storageObjectKey: "nodeB",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  const formatComparatorMessage = (a, b, largestProfile, delta) => {
    if (delta === 0) {
      setCompareWord("equal");
    } else if (a === largestProfile) {
      setCompareWord("larger");
    } else if (b === largestProfile) {
      setCompareWord("smaller");
    }
  };

  const loadComparison = useCallback(async () => {
    try {
      setHasNetworkError(false);
      const comparisonScore = await getProfilerComparator({
        nodeA: nodeA.value,
        nodeB: nodeB.value,
      });
      formatComparatorMessage(
        nodeA.value,
        nodeB.value,
        comparisonScore.data[0].largest,
        comparisonScore.data[0].profile_magnitude_delta
      );

      propagnateFunctionRef.current(comparisonScore.data);
      setData(comparisonScore.data[0]);
    } catch (e) {
      console.error(`Failed to load comparison: ${e}`);
      setHasNetworkError(true);
    }
  }, [nodeA, nodeB]);

  useEffect(() => {
    if (nodeA !== null || nodeB !== null) {
      saveToState("profileComparatorState", {
        nodeA: nodeA,
        nodeB: nodeB,
      });
    }
  }, [nodeA, nodeB]);

  useEffect(() => {
    if (nodeA === null || nodeB === null) return;
    (async () => {
      setLoadingData(true);
      await loadComparison();
      setLoadingData(false);
    })();
  }, [loadComparison, nodeA, nodeB]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}
      {!isLoading && entities && proxies && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8 w-full">
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node A</p>
              <Select
                isSearchable={true}
                value={nodeA}
                onChange={(selected) => setNodeA(selected)}
                options={[...entities, ...proxies].map((node) => {
                  return {
                    label: node.name,
                    value: node.uuid,
                  };
                })}
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node B</p>
              <Select
                isSearchable={true}
                value={nodeB}
                onChange={(selected) => setNodeB(selected)}
                options={[...entities, ...proxies].map((node) => {
                  return {
                    label: node.name,
                    value: node.uuid,
                  };
                })}
              />
            </div>
          </div>
          <div>
            <button
              onClick={() => resetFilters()}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center content-center">
        {loadingData && <p>Loading data...</p>}
        {!loadingData && data && nodeA && nodeB && !hasNetworkError && (
          <div className="w-full flex flex-col items-center content-center gap-8">
            <GaugePlot
              title={"Profile Similarity"}
              value={data?.profile_similarity}
            />
            <p>
              <span>
                <span className="font-bold">{nodeA.label}</span> profile
                strenght is{" "}
              </span>
              {compareWord === "equal" && <span>{compareWord} to</span>}
              {(compareWord === "larger" || compareWord === "smaller") && (
                <span>
                  <span
                    className={`font-bold p-2 rounded-md mx-2 ${
                      data?.profile_magnitude_delta >= 0 &&
                      data?.profile_magnitude_delta <= 33
                        ? "bg-[#3b82f6]"
                        : data?.profile_magnitude_delta > 33 &&
                          data?.profile_magnitude_delta <= 66
                        ? "bg-[orange] text-[black]"
                        : "bg-[red]"
                    }`}
                  >
                    {data?.profile_magnitude_delta}%
                  </span>
                  <span>{compareWord} than</span>
                </span>
              )}{" "}
              <span className="font-bold">{nodeB.label}</span>
            </p>
          </div>
        )}
        {hasNetworkError && <p>Failed to load comparison data</p>}
      </div>
    </div>
  );
};

export default ProfileComparatorFilters;
